import { CustomUnitsDefinition, TaskInventory as TaskInventoryType } from '@bringg/types';
import { cloneDeep as _cloneDeep, isEmpty as _isEmpty, sumBy as _sumBy } from 'lodash';
interface AllTaskInventoriesInWayPoint {
	[key: number]: TaskInventoryType;
}

export declare type CustomUnitsDefinitionWithSum = CustomUnitsDefinition & {
	[key: string]: {
		sum: number;
	};
};

export declare type TaskInventoryTotals = {
	items: number;
	weight: number;
	handling_units: CustomUnitsDefinitionWithSum;
};

export const getAllChildTaskInventoryIds = (inventories: TaskInventoryType[]): string[] => {
	let inventoriesIds = [];
	const extractChildInventoriesIds = (inventoriesToExtract: TaskInventoryType[]) => {
		if (inventoriesToExtract?.length) {
			inventoriesToExtract.forEach(childInventory => {
				inventoriesIds.push(childInventory.id.toString());
				extractChildInventoriesIds(childInventory.inventories);
			});
		}
	};
	extractChildInventoriesIds(inventories);
	return inventoriesIds;
};

export const getAllTaskInventoryChildren = (inventory: TaskInventoryType): TaskInventoryType[] => {
	let inventories = [];
	const extractChildInventories = (inventoryToExtract: TaskInventoryType) => {
		if (inventoryToExtract?.inventories?.length) {
			inventoryToExtract.inventories.forEach(childInventory => {
				inventories.push(childInventory);
				extractChildInventories(childInventory);
			});
		}
	};
	extractChildInventories(inventory);
	return inventories;
};

export const getTaskInventoryTotals = (
	inventory: TaskInventoryType,
	customUnitsDefinition: CustomUnitsDefinition,
	doNotMultipleWeightByQuantity: boolean = false
): TaskInventoryTotals => {
	const has_handling_units = typeof customUnitsDefinition === 'object' && !_isEmpty(customUnitsDefinition);
	let items = 0;
	let weight = 0;
	let handling_units: CustomUnitsDefinition = {};

	if (has_handling_units) {
		handling_units = _cloneDeep(customUnitsDefinition);
		for (const key in handling_units) {
			handling_units[key]['sum'] = 0;
		}
	}

	const extractInventoriesWeightAndHandlingUnits = (inventoryToExtract: TaskInventoryType) => {
		weight += doNotMultipleWeightByQuantity
			? inventoryToExtract.weight || 0
			: (inventoryToExtract.weight || 0) * (inventoryToExtract.original_quantity || 0);

		if (has_handling_units) {
			let inventoryHandlingUnits = inventoryToExtract.handling_units;
			if (inventoryHandlingUnits) {
				for (const key in handling_units) {
					if (inventoryHandlingUnits[key]) {
						handling_units[key]['sum'] += inventoryHandlingUnits[key] || 0;
					}
				}
			}
		}

		if (inventoryToExtract?.inventories?.length) {
			inventoryToExtract.inventories.forEach(childInventory => {
				extractInventoriesWeightAndHandlingUnits(childInventory);
			});
		}
	};

	extractInventoriesWeightAndHandlingUnits(inventory);

	if (inventory?.inventories?.length) {
		items = _sumBy(inventory.inventories.map(childInventory => childInventory.original_quantity));
	}

	return { items, weight, handling_units: handling_units as CustomUnitsDefinitionWithSum };
};

export const getAllTaskInventoryInWayPoint = (
	allInventoriesInTask: TaskInventoryType[],
	wayPointDataId: number
): AllTaskInventoriesInWayPoint => {
	const allTaskInventoriesInWayPoint: AllTaskInventoriesInWayPoint = {};
	allInventoriesInTask?.forEach(taskInventory => {
		if (taskInventory.way_point_id === wayPointDataId) {
			allTaskInventoriesInWayPoint[taskInventory.id] = taskInventory;
		}
	});

	return allTaskInventoriesInWayPoint;
};

export const isClosing = (currentOpenKeys, previousOpenKeys): boolean => {
	return currentOpenKeys.length < previousOpenKeys.length;
};

//for handling closing children when parent gets closed
export const getClosedItemChildren = (keys, key: string | string[], taskInventories: TaskInventoryType[]) => {
	const closedItem = keys.find(item => !key.includes(item));
	const taskInventory = taskInventories.filter(item => item.id === parseInt(closedItem))[0];
	const closedItemChildren = taskInventory ? getAllChildTaskInventoryIds(taskInventory.inventories) : [];
	return closedItemChildren;
};

export const handleShownFields = (allTaskInventoriesInWayPoint, shownFields, setShownFields, customUnitsDefinition) => {
	for (const key in allTaskInventoriesInWayPoint) {
		for (const field in shownFields) {
			if (shownFields[field]) continue;
			const showDimensions =
				field === 'dimensions' &&
				(allTaskInventoriesInWayPoint[key]['width'] ||
					allTaskInventoriesInWayPoint[key]['height'] ||
					allTaskInventoriesInWayPoint[key]['length']);
			if (
				showDimensions ||
				(allTaskInventoriesInWayPoint[key][field] && allTaskInventoriesInWayPoint[key][field]?.length !== 0)
			) {
				setShownFields(prevState => {
					return {
						...prevState,
						[field]: true
					};
				});
			}
		}
		for (const handlingUnitsKey in customUnitsDefinition) {
			if (shownFields[handlingUnitsKey]) continue;
			if (allTaskInventoriesInWayPoint[key]?.handling_units?.hasOwnProperty(handlingUnitsKey)) {
				setShownFields(prevState => {
					return {
						...prevState,
						[handlingUnitsKey]: true
					};
				});
			}
		}
	}
};
